import _fetch from '@avalara/bat-apps-shared/lib/client-fetch';

import { isOneWebDomainRuntime } from '../helpers';
import { checkIsAuthenticated } from './helpers';

/** Net-new trial offers do not require a bearer token for BFF calls. */
export const isNetNewTrialOfferPath = (): boolean =>
  /\/buy\/(free-trial|avatax-free-trial)(\/|$)/.test(window.location.pathname);

let tokenPromise: Promise<string | null> | null = null;

export const resetOwdBearerTokenCache = (): void => {
  tokenPromise = null;
};

export const getOwdBearerToken = (): Promise<string | null> => {
  if (!isOneWebDomainRuntime() || !checkIsAuthenticated() || isNetNewTrialOfferPath()) {
    return Promise.resolve(null);
  }

  // Memoize the in-flight promise itself, not just an eventual value: fetch.ts calls this on
  // every request, and several requests fire concurrently at page mount. A boolean-flag cache set
  // synchronously before the fetch resolves lets a concurrent caller see "resolved" while the
  // token is still null, sending its request with no Authorization header. Sharing one promise
  // means every concurrent caller awaits the same resolution instead of racing it.
  if (!tokenPromise) {
    tokenPromise = (async () => {
      try {
        const accessTokenRes = await _fetch<{ accessToken: string }>({
          url: `${window.AvaAppConfig?.hostnames.avaApp}/api/token`,
          params: { credentials: 'include' },
        });

        return accessTokenRes.ok && accessTokenRes.value.accessToken ? accessTokenRes.value.accessToken : null;
      } catch (error) {
        // _fetch normally converts network/transport failures into a Result with ok: false, but
        // something above it (e.g. the OWD bearer-token endpoint call itself) can still reject
        // outright. Without this, that rejection becomes the memoized tokenPromise forever -- every
        // future call would reject too, with no way out short of resetOwdBearerTokenCache() (not
        // currently called anywhere -- a failure here is otherwise permanent for the rest of the
        // page's lifetime). Converting to a resolved null keeps the single-flight memoization but
        // matches the existing "cache a failed token response" behavior for the ok:false case above.
        // No aui.logger here -- that's server-only in this repo; console.error matches how other
        // client-side code in this file's neighborhood (e.g. side-effects/avatax.ts) surfaces a
        // swallowed failure.
        console.error('getOwdBearerToken: token fetch rejected', error);
        return null;
      }
    })();
  }

  return tokenPromise;
};
